import { useCallback } from 'react';

import { startNewChatDraft } from 'src/components/ai/chat/chat-state/chat-state-actions';
import type { PinnedSkill } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorSkill/utils';
import { buildSkillMarkdownLink } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorSkill/utils';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

const EDIT_UNPINNED_SKILL_PROMPT = 'Edit this skill: **[describe the change]**';

/**
 * Opens a new chat with a draft that asks the agent to edit this skill, its chip pinned in
 * the prompt. The published version is pinned when there is one, since the agent edits the
 * skill rather than the pinned version. A skill with no version yet, such as one that has
 * no description, still opens the chat: the skill editor page tells the agent which skill.
 */
export function useEditSkillWithAi({
  getSkill,
  resolvePinnedSkill,
}: {
  getSkill: () => { isComplete: boolean } | null;
  resolvePinnedSkill: (options: { preferPublished: boolean }) => Promise<PinnedSkill | null>;
}) {
  const { openDrawer } = useGlobalDrawer();
  const editSkillWithAi = useCallback(async () => {
    const pinnedSkill = await resolvePinnedSkill({ preferPublished: true });
    if (!pinnedSkill) {
      const skill = getSkill();
      // A complete skill that could not be pinned already reported the failure.
      if (!skill || skill.isComplete) return;
    }

    const prompt = pinnedSkill
      ? `Edit ${buildSkillMarkdownLink(pinnedSkill)}: **[describe the change]**`
      : EDIT_UNPINNED_SKILL_PROMPT;
    startNewChatDraft(prompt);
    aiThreadIdVar(null);
    openDrawer();
  }, [getSkill, openDrawer, resolvePinnedSkill]);

  return { editSkillWithAi };
}
